import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://leo-api-sagi.staging.sygecom.com.br/api/v1'

const deriveV2BaseUrl = (v1: string): string => {
  if (/\/api\/v1\/?$/.test(v1)) return v1.replace(/\/api\/v1\/?$/, '/api/v2')
  if (/\/v1\/?$/.test(v1)) return v1.replace(/\/v1\/?$/, '/v2')
  return v1.replace(/\/?$/, '/api/v2')
}

const API_V2_BASE_URL = import.meta.env.VITE_API_V2_BASE_URL || deriveV2BaseUrl(API_BASE_URL)

// Token armazenado
let authToken: string | null = null

const attachBearer = (config: InternalAxiosRequestConfig) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`
  }
  return config
}

const logResponseError = (error: AxiosError) => {
  if (error.response) {
    const status = error.response.status

    switch (status) {
      case 401:
        console.error('Token inválido ou expirado')
        break
      case 403:
        console.error('Acesso negado')
        break
      case 404:
        console.error('Recurso não encontrado')
        break
      case 413:
        console.error('Arquivo muito grande')
        break
      case 500:
        console.error('Erro interno do servidor')
        break
      default:
        console.error('Erro na requisição:', error.message)
    }
  } else if (error.request) {
    console.error('Sem resposta do servidor')
  } else {
    console.error('Erro ao configurar requisição:', error.message)
  }

  return Promise.reject(error)
}

// Criar instância do Axios (v1)
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

api.interceptors.request.use(attachBearer, (error: AxiosError) => Promise.reject(error))
api.interceptors.response.use((response) => response, logResponseError)

// Instância v2 — usa o mesmo Bearer gerenciado em memória
export const apiV2: AxiosInstance = axios.create({
  baseURL: API_V2_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

apiV2.interceptors.request.use(attachBearer, (error: AxiosError) => Promise.reject(error))
apiV2.interceptors.response.use((response) => response, logResponseError)

// Funções para gerenciar o token
export const setAuthToken = (token: string): void => {
  authToken = token
}

export const getAuthToken = (): string | null => {
  return authToken
}

export const clearAuthToken = (): void => {
  authToken = null
}

export default api
